import { storage } from '../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

class UploadService {
  // Compress image to WebP with max dimensions for admin (products/categories)
  async compressImageIfNeeded(file, {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    targetMaxBytes = 400 * 1024,
    convertToWebP = true,
  } = {}) {
    try {
      // Skip compression for GIF (could be animated) or already small files (< target)
      if (file.type === 'image/gif' || file.size <= targetMaxBytes) return file;

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = URL.createObjectURL(file);
      });

      let { width, height } = img;
      // Maintain aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const mimeType = convertToWebP ? 'image/webp' : file.type;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
      if (!blob) return file; // Fallback

      // If compressed larger (rare), keep original
      if (blob.size >= file.size) return file;

      const newName = file.name.replace(/\.[a-zA-Z0-9]+$/, convertToWebP ? '.webp' : '.$1');
      return new File([blob], newName, { type: blob.type, lastModified: Date.now() });
    } catch (e) {
      console.warn('Image compression failed, using original file:', e);
      return file; // Fail safe
    }
  }
  // Upload product images with organized folder structure
  async uploadProductImages(files, productName, onProgress) {
    const uploadPromises = files.map((file, index) => {
      return this.uploadSingleProductImage(file, productName, index, onProgress);
    });

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  }

  // Upload a single product image
  async uploadSingleProductImage(file, productName, index, onProgress) {
    return new Promise((resolve, reject) => {
      // Validate file
      if (!this.validateFile(file)) {
        reject(new Error(`Invalid file: ${file.name}`));
        return;
      }

      const prepareAndUpload = async () => {
        // Compress (admin product/category only)
        const compressed = await this.compressImageIfNeeded(file);

        // Generate file path with organized folder structure
        const timestamp = Date.now();
        const sanitizedProductName = productName
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');

        const fileExtension = compressed.name.split('.').pop();
        const fileName = `${sanitizedProductName}-${timestamp}-${index + 1}.${fileExtension}`;
        const filePath = `products/${sanitizedProductName}/${fileName}`;

        // Create storage reference
        const storageRef = ref(storage, filePath);

        // Upload file
        const uploadTask = uploadBytesResumable(storageRef, compressed);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) onProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      };

      prepareAndUpload();
    });
  }

  // Upload customer images for orders
  async uploadCustomerImages(files, orderId, onProgress) {
    const uploadPromises = files.map((file, index) => {
      return this.uploadSingleCustomerImage(file, orderId, index, onProgress);
    });

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading customer images:', error);
      throw error;
    }
  }

  // Upload a single customer image
  async uploadSingleCustomerImage(file, orderId, index, onProgress) {
    return new Promise((resolve, reject) => {
      // Validate file
      if (!this.validateFile(file)) {
        reject(new Error(`Invalid file: ${file.name}`));
        return;
      }

      // Generate file path
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `customer-image-${timestamp}-${index + 1}.${fileExtension}`;
      const filePath = `orders/${orderId}/customer-images/${fileName}`;

      // Create storage reference
      const storageRef = ref(storage, filePath);

      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Track progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  // Upload customer images for cart items (temporary storage)
  async uploadCartCustomizationImages(files, userId, productId, onProgress) {
    const uploadPromises = files.map((file, index) => {
      return this.uploadSingleCartCustomizationImage(file, userId, productId, index, onProgress);
    });

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading cart customization images:', error);
      throw error;
    }
  }

  // Upload a single cart customization image
  async uploadSingleCartCustomizationImage(file, userId, productId, index, onProgress) {
    return new Promise((resolve, reject) => {
      // Validate file
      if (!this.validateFile(file)) {
        reject(new Error(`Invalid file: ${file.name}`));
        return;
      }

      // Generate file path for temporary cart storage
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `cart-custom-${timestamp}-${index + 1}.${fileExtension}`;
      const filePath = `temp-cart/${userId}/${productId}/${fileName}`;

      // Create storage reference
      const storageRef = ref(storage, filePath);

      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Track progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress, index);
          }
        },
        (error) => {
          console.error('Cart customization image upload error:', error);
          reject(error);
        },
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              url: downloadURL,
              path: filePath,
              originalName: file.name
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  // Validate file before upload
  validateFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (file.size > maxSize) {
      alert(`File ${file.name} is too large. Maximum size is 10MB.`);
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      alert(`File ${file.name} has an invalid type. Only JPG, PNG, GIF, and WebP are allowed.`);
      return false;
    }

    return true;
  }

  // Get optimized image URL (for future use with Firebase Storage image optimization)
  getOptimizedImageUrl(originalUrl, size = 'medium') {
    // For now, return original URL
    // In the future, you can add Firebase Storage image resize functionality
    return originalUrl;
  }

  // Delete file from storage
  async deleteFile(fileUrl) {
    try {
      const storageRef = ref(storage, fileUrl);
      await deleteObject(storageRef);
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
}

export default new UploadService();
